'use strict';
import {SyncStatus, ViewMode} from '../enums';


/**
 * @typedef {Object} ReduxObservableItemReducers
 * @property {Object} initialState
 * @property {Function} reducer
 * @property {Function} hasValidId
 */

/**
 * @typedef {Object} ReduxObservableItemState
 * @property {Boolean} init
 * @property {ViewMode} mode
 * @property {T} item
 * @property {?Error} err The latest error from attempting to fetch list data
 * @property {String} id Stores the current model id that is being observed.
 * @property {Boolean} deleting The current model is being deleted. Exists when useDelete==true.
 */

/**
 * @typedef {Object} ReduxObservableItemState~update
 * @property {SyncStatus} status
 */

/**
 * @param {ReduxObservableItemActions} actions
 * @param {ReduxObservableItemOptions} options
 * @return {ReduxObservableItemReducers}
 */
export function buildReducer(actions, options) {
  /** @type {ReduxObservableItemState} */
  const initialState = Object.assign({
    init: false,
    mode: ViewMode.LOADING,
    id: null,
    err: null,
  }, options.initialState || {});

  if (options.useDelete)
    initialState.isDeleting = false;

  return {
    initialState,
    reducer,
    hasValidId,
  };

  /**
   * @param {ReduxObservableItemState} state
   * @param {ReduxAction} action
   * @returns {ReduxObservableItemState}
   */
  function reducer(state = initialState, action) {
    switch (action.type) {
      case actions.hydrateState.TYPE:
        return hasValidId(state, action) ? action.payload : state;

      case actions.initItem.TYPE:
        return initItemReducer(state, action);

      case actions.syncItemSuccess.TYPE:
        return syncItemSuccessReducer(state, action);

      case actions.loadItemSuccess.TYPE:
        return loadItemSuccessReducer(state, action);

      case actions.loadItemError.TYPE:
        return hasValidId(state, action) ? {
          ...state,
          mode: action.payload.mode,
          err: action.payload.err,
        } : state;

      case actions.destroyItem.TYPE:
        return hasValidId(state, action) ? initialState : state;

      case actions.deleteItem.TYPE:
      case actions.deleteItemError.TYPE:
        return deleteItemReducer(state, action);

      default:
        return state;
    }
  }

  /**
   * @param {ReduxObservableItemState} state
   * @param {ReduxAction<InitItemPayload>} action
   * @returns {ReduxObservableItemState}
   */
  function initItemReducer(state, action) {
    /** @type {InitItemPayload} */
    const payload = action.payload;
    let nextState = {
      ...state,
    };

    // force cleanup if another list was loaded, then report as an error
    if (state.init && state.id !== payload.id) {
      nextState = {
        ...initialState,
      };
      options.logger.error(`redux-observable-item reducer initItem called with new id without destroyItem first.`);
    }

    nextState.id = payload.id;

    if (!state.init) {
      nextState.init = true;
      nextState.mode = ViewMode.LOADING;
      nextState.err = null;
    }

    if (action.payload.existingItem) {
      nextState.item = action.payload.existingItem;
      nextState.mode = ViewMode.SUCCESS;
    }

    return nextState;
  }

  /**
   * @param {ReduxObservableItemState} state
   * @param {ReduxAction<*>} action
   */
  function hasValidId(state, action) {
    const id = action.meta && action.meta.id;
    return id === state.id;
  }

  /**
   * @param {ReduxObservableItemState} state
   * @param {ReduxAction<SyncListSuccessPayload>} action
   */
  function syncItemSuccessReducer(state, action) {
    if (!hasValidId(state, action))
      return state;

    const nextState = loadItemSuccessReducer(state, action);

    if (state.mode === ViewMode.LOADING) {
      nextState.mode = ViewMode.SUCCESS;
    }

    return nextState;
  }

  /**
   * @param state
   * @param {ReduxAction<LoadSuccessPayload>} action
   */
  function loadItemSuccessReducer(state, action) {
    if (!hasValidId(state, action))
      return state;

    return {
      ...state,
      item: action.payload.item,
      mode: ViewMode.SUCCESS,
      err: null,
    };
  }

  /**
   * @param {ReduxObservableItemState} state
   * @param {ReduxAction} action
   * @return {ReduxObservableItemState}
   */
  function deleteItemReducer(state, action) {
    if (!hasValidId(state, action))
      return state;

    const item = state.item;
    switch (action.type) {
      case actions.deleteItem.TYPE:
        return {
          ...state,
          isDeleting: true,
          item: {...item, isDeleting: true},
        };

      case actions.deleteItemError.TYPE:
        // do nothing if state IS the initialState because we definitely do not have a channel to update
        return state === initialState ? initialState : {
          ...state,
          isDeleting: false,
          item: {...item, isDeleting: false, deleteItemError: action.payload.err},
        };

      default:
        return state;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/item/reducers.js